import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Edit, Star, StarOff, Copy, Trash2, AlertCircle, RefreshCw } from 'lucide-react';
import { MainLayout } from '@/components/MainLayout';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useCustomReport, useToggleFavoriteReport, useDuplicateCustomReport, useDeleteCustomReport } from '@/hooks/useCustomReports';
import { useReportQuery } from '@/hooks/useReportQuery';
import { useReportExport } from '@/hooks/useReportExport';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CustomReportVisualization } from './components/CustomReportVisualization';

function getVisualizationTitle(type: string): string {
  switch (type) {
    case 'table':
      return 'Tabela de Dados';
    case 'bar':
      return 'Gráfico de Barras';
    case 'line':
      return 'Gráfico de Linhas';
    case 'pie':
      return 'Gráfico de Pizza';
    case 'cards':
      return 'Resumo Executivo';
    default:
      return 'Visualização';
  }
}

export function CustomReportViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: report, isLoading, error: reportError, refetch: refetchReport } = useCustomReport(id || '');

  // Only execute query when report config is available and has dataSources
  const shouldFetchQuery = report?.config && report.config.dataSources?.length > 0;
  const { data: queryResult, isLoading: isLoadingData, error: queryError, refetch: refetchQuery } = useReportQuery(
    shouldFetchQuery ? report.config : null
  );
  const toggleFavorite = useToggleFavoriteReport();
  const duplicateReport = useDuplicateCustomReport();
  const deleteReport = useDeleteCustomReport();
  const { exportReport } = useReportExport();

  if (isLoading) {
    return (
      <MainLayout>
        <SEO title="Carregando relatório..." />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Carregando relatório...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Report Error State
  if (reportError) {
    return (
      <MainLayout>
        <SEO title="Erro ao carregar relatório" />
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 p-6">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Erro ao carregar relatório</AlertTitle>
            <AlertDescription>
              Não foi possível carregar os dados do relatório. O relatório pode ter sido excluído ou você não tem permissão para acessá-lo.
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button onClick={() => refetchReport()} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar Novamente
            </Button>
            <Button onClick={() => navigate('/reports/custom')}>
              Voltar para Relatórios
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!report) {
    return (
      <MainLayout>
        <SEO title="Relatório não encontrado" />
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <p className="text-muted-foreground">Relatório não encontrado</p>
          <Button onClick={() => navigate('/reports/custom')}>
            Voltar para Relatórios
          </Button>
        </div>
      </MainLayout>
    );
  }

  const handleExport = (format: 'pdf' | 'excel' | 'csv') => {
    if (queryResult) {
      exportReport(queryResult, format, {
        filename: `${report.name.replace(/\s+/g, '_')}_${format}`,
      });
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite.mutate(id || '');
  };

  const handleDuplicate = () => {
    duplicateReport.mutate(id || '', {
      onSuccess: () => toast.success('Relatório duplicado com sucesso!'),
    });
  };

  const handleDelete = () => {
    if (confirm('Tem certeza que deseja excluir este relatório?')) {
      deleteReport.mutate(id || '', {
        onSuccess: () => navigate('/reports/custom'),
      });
    }
  };

  const handleEdit = () => {
    navigate(`/reports/custom/${id}/edit`);
  };

  return (
    <MainLayout>
      <SEO
        title={report.name}
        description={report.description || `Relatório personalizado: ${report.name}`}
      />
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/reports/custom')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{report.name}</h1>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleFavorite}
              >
                {report.is_favorite ? (
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ) : (
                  <StarOff className="h-5 w-5" />
                )}
              </Button>
            </div>
            {report.description && (
              <p className="text-muted-foreground mt-1">{report.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button variant="outline" onClick={handleDuplicate}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicar
          </Button>
          <div className="flex items-center border rounded-md">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-r-none border-r"
              onClick={() => handleExport('pdf')}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Report Metadata */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Período:</span>{' '}
              <span className="font-medium">
                {report.config.dateRange.type === 'this_month' && 'Este Mês'}
                {report.config.dateRange.type === 'last_month' && 'Mês Passado'}
                {report.config.dateRange.type === 'today' && 'Hoje'}
                {report.config.dateRange.type === 'this_week' && 'Esta Semana'}
                {report.config.dateRange.type === 'custom' && (
                  <>
                    {format(new Date(report.config.dateRange.start!), "dd/MM/yyyy", { locale: ptBR })}
                    {' - '}
                    {format(new Date(report.config.dateRange.end!), "dd/MM/yyyy", { locale: ptBR })}
                  </>
                )}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Tipo:</span>{' '}
              <span className="font-medium capitalize">
                {report.config.filters.transactionType === 'all' && 'Todas'}
                {report.config.filters.transactionType === 'income' && 'Receitas'}
                {report.config.filters.transactionType === 'expense' && 'Despesas'}
              </span>
            </div>
            {report.config.groupBy && (
              <div>
                <span className="text-muted-foreground">Agrupado por:</span>{' '}
                <span className="font-medium">
                  {report.config.groupBy === 'category' && 'Categoria'}
                  {report.config.groupBy === 'cost_center' && 'Centro de Custo'}
                  {report.config.groupBy === 'client' && 'Cliente'}
                  {report.config.groupBy === 'supplier' && 'Fornecedor'}
                  {report.config.groupBy === 'month' && 'Mês'}
                  {report.config.groupBy === 'week' && 'Semana'}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoadingData && (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Carregando dados...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Query Error State */}
      {!isLoadingData && queryError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar dados</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              Ocorreu um erro ao buscar os dados do relatório. Verifique se os filtros estão corretos.
            </span>
            <Button
              onClick={() => refetchQuery()}
              variant="outline"
              size="sm"
              className="ml-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar Novamente
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Report Content */}
      {!isLoadingData && queryResult && (
        <Card>
          <CardHeader>
            <CardTitle>{getVisualizationTitle(report.config.visualizationType)}</CardTitle>
          </CardHeader>
          <CardContent>
            <CustomReportVisualization
              data={queryResult.data}
              config={report.config}
              comparisonData={queryResult.comparison?.previous?.data}
            />
          </CardContent>
        </Card>
      )}

      {/* No Data State */}
      {!isLoadingData && queryResult && queryResult.data.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <p className="text-muted-foreground">Nenhum dado encontrado para os filtros selecionados</p>
          </CardContent>
        </Card>
      )}
    </div>
    </MainLayout>
  );
}
